// src/modules/goldVault/pages/PurchaseLedger.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Card, Row, Col, Input, Select, DatePicker, Button,
  Tag, Typography, Space, Spin,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getPurchaseLedger } from '../api';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranches } from '../../branches/api';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const INV_STATUS_COLOR = {
  PENDING: 'default', IN_BRANCH_VAULT: 'gold', IN_TRANSFER: 'blue',
  IN_HO_VAULT: 'purple', BATCHED: 'green',
};

const fmtW = (v) => v != null ? `${Number(v).toFixed(3)}g` : '—';
const fmtCur = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function PurchaseLedger() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ branch: '', search: '', dateRange: null });

  useEffect(() => {
    fetchBranches().then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [pagination.current, pagination.pageSize]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...(filters.branch ? { branch: filters.branch } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.dateRange?.[0] ? { startDate: filters.dateRange[0].toISOString() } : {}),
        ...(filters.dateRange?.[1] ? { endDate: filters.dateRange[1].toISOString() } : {}),
      };
      const res = await getPurchaseLedger(params);
      setData(res.data || []);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Case No.', dataIndex: 'caseNumber', key: 'caseNumber', width: 120,
      render: (v, r) => (
        <Button type="link" size="small" onClick={() => navigate(`/vault/purchase-ledger/${r.caseType === 'WALK_IN' ? 'walk-in' : 'pledge'}/${r._id}`)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Type', dataIndex: 'caseType', key: 'caseType', width: 80,
      render: (v) => <Tag color={v === 'WALK_IN' ? 'blue' : 'orange'}>{v === 'WALK_IN' ? 'Walk-In' : 'Pledge'}</Tag>,
    },
    {
      title: 'Customer', key: 'customer', width: 150,
      render: (_, r) => r.customer ? `${r.customer.name}` : '—',
    },
    {
      title: 'Phone', key: 'phone', width: 120,
      render: (_, r) => r.customer?.phone || '—',
    },
    {
      title: 'Branch', key: 'branch', width: 100,
      render: (_, r) => r.branch?.name || '—',
    },
    {
      title: 'Purchase Date', dataIndex: 'purchaseDate', key: 'purchaseDate', width: 120,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    { title: 'Gross Wt', dataIndex: 'grossWeight', key: 'grossWeight', width: 90, render: fmtW },
    { title: 'Net Wt', dataIndex: 'netWeight', key: 'netWeight', width: 90, render: fmtW },
    {
      title: 'Purchase Cost', dataIndex: 'purchaseCost', key: 'purchaseCost', width: 130,
      render: fmtCur,
    },
    {
      title: 'Vault Status', dataIndex: 'inventoryStatus', key: 'inventoryStatus', width: 120,
      render: (v) => <Tag color={INV_STATUS_COLOR[v] || 'default'}>{v || 'PENDING'}</Tag>,
    },
    {
      title: 'Transfer', key: 'transfer', width: 120,
      render: (_, r) => r.transfer
        ? <Button type="link" size="small" onClick={() => navigate(`/vault/transfers/${r.transfer._id}`)}>{r.transfer.transferNumber}</Button>
        : '—',
    },
    {
      title: 'Batch', key: 'batch', width: 110,
      render: (_, r) => r.batch
        ? <Button type="link" size="small" onClick={() => navigate(`/gold-inventory/${r.batch._id}`)}>{r.batch.batchId}</Button>
        : '—',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Purchase Ledger</Title></Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle" wrap>
          <Col xs={24} sm={8} md={6}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name, phone, case no."
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={5}>
            <Select
              placeholder="All Branches"
              allowClear
              style={{ width: '100%' }}
              value={filters.branch || undefined}
              onChange={(v) => setFilters((f) => ({ ...f, branch: v || '' }))}
            >
              {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={10} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => setFilters((f) => ({
                ...f,
                dateRange: dates ? [dates[0].toDate(), dates[1].toDate()] : null,
              }))}
            />
          </Col>
          <Col>
            <Button type="primary" onClick={() => { setPagination((p) => ({ ...p, current: 1 })); load(); }}>
              Apply
            </Button>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        <Table
          dataSource={data}
          rowKey="_id"
          columns={columns}
          scroll={{ x: 1400 }}
          size="small"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            onChange: (page, size) => setPagination({ current: page, pageSize: size, total: pagination.total }),
          }}
        />
      </Spin>
    </div>
  );
}
